/**
 * Global Search Types
 *
 * Type definitions for the Global Search API.
 * These types support searching across multiple resource types including:
 * - Plans
 * - Workouts
 * - Exercises
 * - Challenges
 * - Friends
 * - Clients
 *
 * @module types/domain/search
 */

// =============================================================================
// BASIC TYPE ALIASES
// =============================================================================

/**
 * Valid search type filters that can be passed to the API.
 */
export type GlobalSearchType =
  'plan' | 'workout' | 'exercises' | 'challenge' | 'friends' | 'clients';

/**
 * Array of valid search types for validation.
 */
export const GLOBAL_SEARCH_TYPES: readonly GlobalSearchType[] = [
  'plan',
  'workout',
  'exercises',
  'challenge',
  'friends',
  'clients',
] as const;

/**
 * Minimum number of characters required for a search term.
 */
export const MIN_SEARCH_TERM_LENGTH = 3;

// =============================================================================
// SEARCH RESULT INTERFACES (Discriminated Union)
// =============================================================================

/**
 * Plan search result.
 */
export interface PlanSearchResult {
  readonly type: 'plan';
  readonly id: string;
  readonly name: string;
  readonly slug: string;
}

/**
 * Workout search result.
 */
export interface WorkoutSearchResult {
  readonly type: 'workout';
  readonly id: string;
  readonly name: string;
  readonly slug: string;
}

/**
 * Exercise search result (from combined repository or custom exercises).
 */
export interface ExerciseSearchResult {
  readonly type: 'exercise';
  readonly id: string;
  readonly name: string;
  readonly target: string;
}

/**
 * Challenge search result.
 */
export interface ChallengeSearchResult {
  readonly type: 'challenge';
  readonly id: string;
  readonly title: string;
}

/**
 * Friend search result.
 */
export interface FriendSearchResult {
  readonly type: 'friend';
  readonly username: string;
  readonly avatar: string | null;
}

/**
 * Client search result (for trainers).
 */
export interface ClientSearchResult {
  readonly type: 'client';
  readonly id: string;
  /** Handle the client view route (`/client/:username`) is keyed on */
  readonly username: string;
  readonly name: string;
}

/**
 * Discriminated union of all possible search result types.
 */
export type GlobalSearchResult =
  | PlanSearchResult
  | WorkoutSearchResult
  | ExerciseSearchResult
  | ChallengeSearchResult
  | FriendSearchResult
  | ClientSearchResult;

// =============================================================================
// API REQUEST / RESPONSE TYPES
// =============================================================================

/**
 * Request parameters for the global search endpoint.
 */
export interface GlobalSearchRequest {
  /** Search term (minimum 3 characters) */
  search: string;
  /** Optional filter to search only a specific type */
  type?: GlobalSearchType | null;
}

/**
 * Metadata returned with search results.
 */
export interface GlobalSearchMetadata {
  /** The search term that was used */
  search: string;
  /** The type filter that was applied (null if searching all) */
  type: GlobalSearchType | null;
  /** Total number of results returned */
  total: number;
  /** Unix timestamp of when the search was performed */
  timestamp: number;
}

/**
 * Full API response from the global search endpoint.
 */
export interface GlobalSearchResponse {
  data: GlobalSearchResult[];
  metadata: GlobalSearchMetadata;
}

/**
 * API error response structure.
 */
export interface GlobalSearchErrorResponse {
  success: false;
  message: string;
  errors?: Record<string, string[]>;
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a value is a valid GlobalSearchType.
 */
export function isGlobalSearchType(value: unknown): value is GlobalSearchType {
  return (
    typeof value === 'string' &&
    GLOBAL_SEARCH_TYPES.includes(value as GlobalSearchType)
  );
}

/**
 * Type guard for PlanSearchResult.
 */
export function isPlanSearchResult(
  result: GlobalSearchResult
): result is PlanSearchResult {
  return result.type === 'plan';
}

/**
 * Type guard for WorkoutSearchResult.
 */
export function isWorkoutSearchResult(
  result: GlobalSearchResult
): result is WorkoutSearchResult {
  return result.type === 'workout';
}

/**
 * Type guard for ExerciseSearchResult.
 */
export function isExerciseSearchResult(
  result: GlobalSearchResult
): result is ExerciseSearchResult {
  return result.type === 'exercise';
}

/**
 * Type guard for ChallengeSearchResult.
 */
export function isChallengeSearchResult(
  result: GlobalSearchResult
): result is ChallengeSearchResult {
  return result.type === 'challenge';
}

/**
 * Type guard for FriendSearchResult.
 */
export function isFriendSearchResult(
  result: GlobalSearchResult
): result is FriendSearchResult {
  return result.type === 'friend';
}

/**
 * Type guard for ClientSearchResult.
 */
export function isClientSearchResult(
  result: GlobalSearchResult
): result is ClientSearchResult {
  return result.type === 'client';
}

/**
 * Type guard to check if a value is a valid GlobalSearchRequest.
 */
export function isGlobalSearchRequest(
  value: unknown
): value is GlobalSearchRequest {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const obj = value as Record<string, unknown>;

  if (typeof obj.search !== 'string') {
    return false;
  }

  if (obj.type !== undefined && obj.type !== null) {
    if (!isGlobalSearchType(obj.type)) {
      return false;
    }
  }

  return true;
}

/**
 * Type guard to check if a value is a valid GlobalSearchResponse.
 */
export function isGlobalSearchResponse(
  value: unknown
): value is GlobalSearchResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const obj = value as Record<string, unknown>;

  return (
    Array.isArray(obj.data) &&
    typeof obj.metadata === 'object' &&
    obj.metadata !== null
  );
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

/**
 * Extract results by type from a mixed results array.
 */
export type FilterResultsByType<T extends GlobalSearchResult['type']> = Extract<
  GlobalSearchResult,
  { type: T }
>;

/**
 * Group search results by their type.
 */
export interface GroupedSearchResults {
  plans: PlanSearchResult[];
  workouts: WorkoutSearchResult[];
  exercises: ExerciseSearchResult[];
  challenges: ChallengeSearchResult[];
  friends: FriendSearchResult[];
  clients: ClientSearchResult[];
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Helper function to group search results by type.
 */
export function groupSearchResults(
  results: GlobalSearchResult[]
): GroupedSearchResults {
  return {
    plans: results.filter(isPlanSearchResult),
    workouts: results.filter(isWorkoutSearchResult),
    exercises: results.filter(isExerciseSearchResult),
    challenges: results.filter(isChallengeSearchResult),
    friends: results.filter(isFriendSearchResult),
    clients: results.filter(isClientSearchResult),
  };
}

/**
 * Creates an empty GroupedSearchResults object.
 */
export function createEmptyGroupedResults(): GroupedSearchResults {
  return {
    plans: [],
    workouts: [],
    exercises: [],
    challenges: [],
    friends: [],
    clients: [],
  };
}

/**
 * Validates a search term meets minimum length requirements.
 */
export function isValidSearchTerm(search: string): boolean {
  return (
    typeof search === 'string' && search.trim().length >= MIN_SEARCH_TERM_LENGTH
  );
}

/**
 * Gets the total count from grouped results.
 */
export function getTotalResultCount(grouped: GroupedSearchResults): number {
  return (
    grouped.plans.length +
    grouped.workouts.length +
    grouped.exercises.length +
    grouped.challenges.length +
    grouped.friends.length +
    grouped.clients.length
  );
}

/**
 * Checks if grouped results are empty.
 */
export function isEmptyResults(grouped: GroupedSearchResults): boolean {
  return getTotalResultCount(grouped) === 0;
}

/**
 * Gets result counts by type.
 */
export function getResultCountsByType(
  grouped: GroupedSearchResults
): Record<GlobalSearchResult['type'], number> {
  return {
    plan: grouped.plans.length,
    workout: grouped.workouts.length,
    exercise: grouped.exercises.length,
    challenge: grouped.challenges.length,
    friend: grouped.friends.length,
    client: grouped.clients.length,
  };
}
